import { html, LitElement, type PropertyValues } from 'lit'
import { customElement, property, state, query } from 'lit/decorators.js'
import { toComparableLowerCase } from './helpers/text-compare'
import { classMap } from 'lit/directives/class-map.js'
import style from './style/zaptic-tag-input-style'

export enum Events {
    tagCreated = 'tagCreated',
    tagSelected = 'tagSelected',
    tagDeselected = 'tagDeselected',
    inputFocused = 'inputFocused',
}

export interface CustomEventPayload {
    [Events.tagCreated]: string
    [Events.tagSelected]: string
    [Events.tagDeselected]: string
    [Events.inputFocused]: void
}

@customElement('zaptic-tag-input')
export class TagInput extends LitElement {
    static styles = style

    @property({ type: Array })
    availableTags: string[]

    @property({ type: Boolean })
    isEditable: boolean = false

    @property({ type: Array, attribute: true })
    selectedTags: string[]

    @property()
    title: string | null = null

    @property()
    isSearchSuggestionHidden: boolean = false

    @state()
    _comparableTagSearchInput: string = ''

    @state()
    _shouldAlignSuggestionsLeft = true

    @state()
    _shouldSuggestNewTag = false

    @state()
    _suggestedTags: string[] = []

    @state()
    _tagSearchInput: string = ''

    @query('.tag-search')
    _searchInputElement: HTMLElement

    render() {
        return html`<div class="${classMap({ container: true, 'has-selected-tags': this.selectedTags.length > 0 })}">
            ${this.title ? html`<div class="title">${this.title}</div>` : null}
            <div class="tags-container">
                ${this.selectedTags.map((tag) => this.SelectedTag(tag))}
                ${this.isEditable
                    ? html`<div class="input-container">${this.TagSearch()}${this.SearchSuggestions()}</div>`
                    : null}
            </div>
        </div>`
    }

    private TagSuggestion(tag: string) {
        const search = toComparableLowerCase(this._tagSearchInput)
        const firstMatchedCharacterIndex = toComparableLowerCase(tag).indexOf(search)
        const afterLastMatchedCharacterIndex = firstMatchedCharacterIndex + search.length
        const beforeMatchedSubstring = tag.substring(0, firstMatchedCharacterIndex)
        const matchedSubstring = tag.substring(firstMatchedCharacterIndex, afterLastMatchedCharacterIndex)
        const afterMatchedSubstring = tag.substring(afterLastMatchedCharacterIndex)
        const tagWithMatchingSubstringMarked = html`${beforeMatchedSubstring}<b><u>${matchedSubstring}</u></b
            >${afterMatchedSubstring}`

        return html`<li
            data-testid="tag-search-suggestion-${tag}"
            class="tag-search-suggestion"
            @click=${() => this.selectTag(tag)}
        >
            ${tagWithMatchingSubstringMarked}
        </li>`
    }

    private NewTagSuggestion() {
        return html`<li class="tag-search-suggestion tag-search-suggestion-new" @click=${this.createTag}>
            <zaptic-icon class="icon" icon-name="add-dashed-circle"></zaptic-icon>
            ${window.i18n('shared:tagInput.createTag', { tag: this._tagSearchInput })}
        </li>`
    }

    private SelectedTag(tag: string) {
        if (this.isEditable) {
            return html`<zaptic-tag
                data-testid="selected-tag-${tag}"
                isRemovable
                @onRemove=${() => this.deselectTag(tag)}
                label=${tag}
            ></zaptic-tag>`
        }
        return html`<zaptic-tag class="no-hover" label=${tag}></zaptic-tag>`
    }

    private TagSearch() {
        return html`<input
            data-testid="tag-search"
            class="tag-search"
            type="text"
            @focus=${this.onInputFocus}
            placeholder="+ ${window.i18n('shared:tagInput.addTag')}"
            @input=${this.onTagSearchInput}
            @keydown=${this.onTagSearchKeydown}
            .value=${this.isSearchSuggestionHidden ? '' : this._tagSearchInput}
            style="width: ${this._tagSearchInput.length}ch"
        />`
    }

    private SearchSuggestions() {
        return !this.isSearchSuggestionHidden && (this._shouldSuggestNewTag || this._suggestedTags.length)
            ? html`<ul
                  class="tag-search-suggestions ${this._shouldAlignSuggestionsLeft
                      ? 'align-to-left'
                      : 'align-to-right'}"
              >
                  ${this._suggestedTags.map((tag) => this.TagSuggestion(tag))}
                  ${this._shouldSuggestNewTag ? this.NewTagSuggestion() : null}
              </ul>`
            : null
    }

    willUpdate(changedProperties: PropertyValues<TagInput>) {
        this._shouldSuggestNewTag =
            this.isSearchNotEmpty() &&
            !this.isAlreadyAvailable(this._tagSearchInput) &&
            !this.isAlreadySelected(this._tagSearchInput)
        if (changedProperties.has('_tagSearchInput')) {
            this._comparableTagSearchInput = toComparableLowerCase(this._tagSearchInput)
        }
        if (
            changedProperties.has('_tagSearchInput') ||
            changedProperties.has('availableTags') ||
            changedProperties.has('selectedTags') ||
            changedProperties.has('_searchInputElement')
        ) {
            this._suggestedTags = this.isSearchNotEmpty()
                ? this.availableTags.filter((tag) => !this.isAlreadySelected(tag) && this.doesMatchSearch(tag))
                : []
            const SUGGESTIONS_LIST_WIDTH = 300
            if (this._searchInputElement) {
                this._shouldAlignSuggestionsLeft =
                    this._searchInputElement.getBoundingClientRect().left + SUGGESTIONS_LIST_WIDTH < window.innerWidth
            }
        }
    }

    private isSearchNotEmpty() {
        return this._tagSearchInput !== ''
    }

    private doesMatchSearch(tag: string) {
        return toComparableLowerCase(tag).includes(this._comparableTagSearchInput)
    }

    private isAlreadySelected(tag: string) {
        return this.selectedTags.includes(tag)
    }

    private isAlreadyAvailable(tag: string) {
        return this.availableTags.includes(tag)
    }

    private onTagSearchInput(inputChangedEvent: any) {
        this._tagSearchInput = inputChangedEvent.target.value
    }

    private onTagSearchKeydown(e: KeyboardEvent) {
        if (e.code === 'Enter' && this._shouldSuggestNewTag) {
            this.createTag()
        }
    }

    private onInputFocus() {
        this.dispatchEvent(new CustomEvent<void>(Events.inputFocused))
    }

    private selectTag(tag: string) {
        this._tagSearchInput = ''
        this.dispatchEvent(
            new CustomEvent<CustomEventPayload['tagSelected']>(Events.tagSelected, {
                detail: tag,
            })
        )
    }

    private deselectTag(tag: string) {
        this.dispatchEvent(
            new CustomEvent<CustomEventPayload['tagDeselected']>(Events.tagDeselected, {
                detail: tag,
            })
        )
    }

    private createTag() {
        this.dispatchEvent(
            new CustomEvent<CustomEventPayload['tagCreated']>(Events.tagCreated, {
                detail: this._tagSearchInput,
            })
        )
        this.selectTag(this._tagSearchInput)
    }
}
